import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect
} from 'react';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenAverageRating from 'common/components/RewindModal/Konvas/ShareableScreenAverageRating';
import Slides from 'common/components/RewindModal/Slides';
import Card from 'common/components/RewindModal/screens/component/Card';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';

type ScreenAverageRatingProps = {
  data: {
    average_rating: number;
    average_rating_phrase_1?: string;
    average_rating_phrase_2?: string;
    average_rating_wording?: string;
  };
  entities: any[];
  allScreens?: any[];
  onNext: () => void;
  isLastStep: boolean;
  trackingScreenName?: string;
};

const shuffleArray = (array: any[]) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const ScreenAverageRatingContent = ({
  data,
  entities,
  allScreens,
  onNext,
  isLastStep,
  isDownloadReady
}: ScreenAverageRatingProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const [layerAEntities, setLayerAEntities] = useState<any[]>([]);
  const [layerBEntities, setLayerBEntities] = useState<any[]>([]);
  const [activeLayer, setActiveLayer] = useState<'A' | 'B'>('A');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const flashbackType = useMemo(() => {
    if (!allScreens) return undefined;
    const flashbackScreen = allScreens.find(s => s.name === 'flashback_type');
    return flashbackScreen?.data?.flashback_type;
  }, [allScreens]);

  const shouldUseWording = useMemo(() => {
    return flashbackType === 6 && !!data?.average_rating_wording;
  }, [flashbackType, data?.average_rating_wording]);

  const allRatedEntities = useMemo(
    () => entities?.[0]?.entities ?? [],
    [entities]
  );

  useEffect(() => {
    const initialEntities = allRatedEntities.slice(0, 6);
    setLayerAEntities(initialEntities);
    if (allRatedEntities.length > 6) {
      const shuffled = shuffleArray(allRatedEntities);
      setLayerBEntities(shuffled.slice(0, 6));
    }
  }, [allRatedEntities]);

  useEffect(() => {
    if (allRatedEntities.length <= 6) return;

    const TRANSITION_DURATION = 200;

    const interval = setInterval(() => {
      const shuffled = shuffleArray(allRatedEntities);
      const newEntities = shuffled.slice(0, 6);

      if (activeLayer === 'A') {
        setLayerBEntities(newEntities);
      } else {
        setLayerAEntities(newEntities);
      }

      setIsTransitioning(true);

      setTimeout(() => {
        setActiveLayer(activeLayer === 'A' ? 'B' : 'A');
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }, 5000);

    return () => clearInterval(interval);
  }, [allRatedEntities, activeLayer]);

  const getTopAndBottomCards = useCallback((entities: any[]) => {
    const length = entities.length;
    let top: any[], bottom: any[];

    if (length === 3) {
      top = entities.slice(0, 2);
      bottom = entities.slice(2, 3);
    } else if (length === 4) {
      top = entities.slice(0, 2);
      bottom = entities.slice(2, 4);
    } else if (length === 5) {
      top = entities.slice(0, 2);
      bottom = entities.slice(2, 5);
    } else {
      top = entities.slice(0, 3);
      bottom = entities.slice(3, 6);
    }

    return { top, bottom };
  }, []);

  const layerATopBottom = useMemo(
    () => getTopAndBottomCards(layerAEntities),
    [layerAEntities, getTopAndBottomCards]
  );

  const layerBTopBottom = useMemo(
    () => getTopAndBottomCards(layerBEntities),
    [layerBEntities, getTopAndBottomCards]
  );

  const shouldShowCovers = useMemo(
    () => allRatedEntities.length >= 3,
    [allRatedEntities.length]
  );

  const topCardsClass = useMemo(
    () => `nb-cards-${layerATopBottom.top.length}`,
    [layerATopBottom.top.length]
  );
  const bottomCardsClass = useMemo(
    () => `nb-cards-${layerATopBottom.bottom.length}`,
    [layerATopBottom.bottom.length]
  );

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = 'ma-note-allocine-2025.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Error handling for image generation
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  return (
    <>
      <Slides>
        <div
          className={classNames(
            styles.contentLayout,
            styles.ratingSlide,
            styles.centeredLayout
          )}
        >
          {shouldShowCovers && (
            <div
              className={classNames(
                styles.cardRow,
                styles.topCardRow,
                topCardsClass
              )}
            >
              {/* Couche A */}
              <div
                className={classNames(
                  styles.cardLayer,
                  activeLayer === 'A' && !isTransitioning
                    ? styles.layerActive
                    : styles.layerInactive,
                  activeLayer === 'A' && isTransitioning && styles.layerFadeOut,
                  activeLayer === 'B' && isTransitioning && styles.layerFadeIn
                )}
              >
                {layerATopBottom.top.map((entity: any, index: number) => (
                  <Card
                    key={`layerA-top-${entity?.id || index}`}
                    path={entity?.poster?.path}
                  />
                ))}
              </div>
              {/* Couche B */}
              <div
                className={classNames(
                  styles.cardLayer,
                  activeLayer === 'B' && !isTransitioning
                    ? styles.layerActive
                    : styles.layerInactive,
                  activeLayer === 'B' && isTransitioning && styles.layerFadeOut,
                  activeLayer === 'A' && isTransitioning && styles.layerFadeIn
                )}
              >
                {layerBTopBottom.top.map((entity: any, index: number) => (
                  <Card
                    key={`layerB-top-${entity?.id || index}`}
                    path={entity?.poster?.path}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={styles.ratingWrapper}>
            <div
              className={classNames(styles.starIcon, 'icon', 'icon-star')}
            ></div>
            <div className={classNames(styles.averageRating, styles.golden)}>
              {data?.average_rating?.toFixed(1) ?? 'N/A'}
            </div>
            {shouldUseWording || data?.average_rating_wording ? (
              <div className={styles.slideTitle}>
                {data?.average_rating_wording}
              </div>
            ) : (
              <>
                <div className={styles.slideTitle}>
                  {data?.average_rating_phrase_1}
                </div>
                <div className={styles.slideText}>
                  {data?.average_rating_phrase_2}
                </div>
              </>
            )}
          </div>

          {shouldShowCovers && (
            <div
              className={classNames(
                styles.cardRow,
                styles.bottomCardRow,
                bottomCardsClass
              )}
            >
              {/* Couche A */}
              <div
                className={classNames(
                  styles.cardLayer,
                  activeLayer === 'A' && !isTransitioning
                    ? styles.layerActive
                    : styles.layerInactive,
                  activeLayer === 'A' && isTransitioning && styles.layerFadeOut,
                  activeLayer === 'B' && isTransitioning && styles.layerFadeIn
                )}
              >
                {layerATopBottom.bottom.map((entity: any, index: number) => (
                  <Card
                    key={`layerA-bottom-${entity?.id || index}`}
                    path={entity?.poster?.path}
                  />
                ))}
              </div>
              {/* Couche B */}
              <div
                className={classNames(
                  styles.cardLayer,
                  activeLayer === 'B' && !isTransitioning
                    ? styles.layerActive
                    : styles.layerInactive,
                  activeLayer === 'B' && isTransitioning && styles.layerFadeOut,
                  activeLayer === 'A' && isTransitioning && styles.layerFadeIn
                )}
              >
                {layerBTopBottom.bottom.map((entity: any, index: number) => (
                  <Card
                    key={`layerB-bottom-${entity?.id || index}`}
                    path={entity?.poster?.path}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <BottomActions
          showShare={true}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          shareText={
            isDownloading
              ? 'Génération...'
              : flashbackType === 6
                ? 'Partager la moyenne'
                : 'Partager ma moyenne'
          }
          showNext={!isLastStep}
          onNextClick={onNext}
        />
      </Slides>

      <ShareableScreenAverageRating
        ref={stageRef}
        data={data}
        entities={entities}
      />
    </>
  );
};

const ScreenAverageRating = (props: ScreenAverageRatingProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenAverageRatingContent
        {...props}
        isDownloadReady={isDownloadReady}
      />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenAverageRating;
